import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import type { ExperienceCategories, ExperienceInterests } from "@/types/generated/MetadataExperiencesApi";
import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";

export const EXPERIENCE_CATEGORIES_INTERESTS_QUERY_KEY = "experience-curation-categories-interests";

export interface CategoriesInterestsQueryData {
  categories: string[];
  categoriesId: string | undefined;
  interests: string[];
  interestsId: string | undefined;
}

export function useExperienceCategoriesInterestsQuery(experienceId: Ref<string>) {
  const { get } = useMetadataExperienceApi();

  return useQuery({
    queryKey: [EXPERIENCE_CATEGORIES_INTERESTS_QUERY_KEY, experienceId],
    queryFn: async (): Promise<CategoriesInterestsQueryData> => {
      const params = {
        filters: emit(builder.eq("experience_id", experienceId.value)),
      };

      const [categoriesRes, interestsRes] = await Promise.all([
        get<ExperienceCategories[]>("experience-categories", { params: { ...params, fields: "categories" } }),
        get<ExperienceInterests[]>("experience-interests", { params: { ...params, fields: "interests" } }),
      ]);

      return {
        categories: categoriesRes.data[0]?.categories ?? [],
        categoriesId: categoriesRes.data[0]?.id,
        interests: interestsRes.data[0]?.interests ?? [],
        interestsId: interestsRes.data[0]?.id,
      };
    },
    enabled: computed(() => !!experienceId.value),
  });
}

export function useCurrentExperienceCategoriesInterestsQuery() {
  const experienceId = useRouteParams<string>("id");
  return useExperienceCategoriesInterestsQuery(experienceId);
}
